import {PRICES_SEARCH_SHOW_MORE_MAX_CITIES} from "../../constants/prices_search";
import type {
    IParsedPricesSearchQuery,
    IPricesAddressSearchResult,
    IPricesScopedAddressResult,
    IPricesSearchContext,
    IPricesSearchStreetGroup
} from "../../types/IPricesSearchModel";
import type {IRcnAddressKey, IRcnAddressSuggestion} from "../../types/IRcnAddress";
import {normalizeSearchText} from "../normalize_search_text";
import {compareHouseNumbers, houseNumberStartsWith} from "./compare_house_numbers";
import {formatRcnStreetName, getRcnStreetMatchName} from "./format_rcn_display_name";
import {findPricesCityByName} from "./resolve_query_diacritics";
import {sanitizeRcnQueryTokens} from "./sanitize_rcn_query_tokens";
import {PRICES_SEARCH_MATCH_SCORE, scoreMatch} from "./score_match";

const CITY_SCOPE_PREFIX = "city:";

/** `${city}|${street}` of the raw RCN values - the street group key and the scope key of its numbers request. */
export const getRcnStreetKey = ({city, street}: Pick<IRcnAddressKey, "city" | "street">): string => `${city}|${street}`;

/** `${city}|${street}|${number}` of the raw RCN values - shared by navigation ids, the recent address id and number prices. */
export const getRcnAddressKey = ({city, street, number}: IRcnAddressKey): string => `${city}|${street}|${number}`;

/** Scope key of a "more streets in this city" request. */
export const getPricesSearchCityScopeKey = (citySlug: string): string => `${CITY_SCOPE_PREFIX}${citySlug}`;

export interface IGroupedRcnAddresses {
    /** Rows of the 14 prices cities: the base response plus the scoped ones for the current `baseKey`, unique by address. */
    rows: IRcnAddressSuggestion[];
    /** Streets that match the query, best first. */
    streets: IPricesSearchStreetGroup[];
    /** Rows of those streets whose number starts with `parsed.numberToken`, best first; empty without a number. */
    addresses: IRcnAddressSuggestion[];
}

export interface IGroupRcnAddressesInput {
    parsed: IParsedPricesSearchQuery;
    base: IPricesAddressSearchResult;
    scoped: readonly IPricesScopedAddressResult[];
    context: IPricesSearchContext;
}

const getCitySlug = (rawCity: string): string | null => findPricesCityByName(rawCity)?.slug ?? null;

// A scoped response is only trusted for what it was asked for: the exact street, or the city.
const getScopedRows = (result: IPricesScopedAddressResult): IRcnAddressSuggestion[] => {
    if (result.scopeKey.startsWith(CITY_SCOPE_PREFIX)) {
        const citySlug = result.scopeKey.slice(CITY_SCOPE_PREFIX.length);

        return result.rows.filter((row) => getCitySlug(row.city) === citySlug);
    }

    return result.rows.filter((row) => getRcnStreetKey(row) === result.scopeKey);
};

// Complete = every number of the street is known: the base response was not capped, or a scoped request for the
// street (with rows of it - an empty answer falls back to the rows we have) or for its city was not capped.
const isStreetComplete = (key: string, citySlug: string, base: IPricesAddressSearchResult, scoped: readonly IPricesScopedAddressResult[]): boolean =>
    !base.capped ||
    scoped.some(
        (result) =>
            !result.capped &&
            ((result.scopeKey === key && result.rows.some((row) => getRcnStreetKey(row) === key)) || result.scopeKey === getPricesSearchCityScopeKey(citySlug))
    );

const compareNumbers = (a: IRcnAddressSuggestion, b: IRcnAddressSuggestion): number => compareHouseNumbers(a.number, b.number);

/**
 * Merges the base and the scoped rows, keeps the 14 prices cities (other cities have no prices
 * page), groups rows into streets, drops RCN's substring noise and ranks what is left - match score, a city named in
 * the query, the city of the current page, then transactions.
 */
export const groupRcnAddresses = ({parsed, base, scoped, context}: IGroupRcnAddressesInput): IGroupedRcnAddresses => {
    const scopedForBase = scoped.filter((result) => result.baseKey === parsed.baseKey);

    const rowsByKey = new Map<string, IRcnAddressSuggestion>();
    const rowsByStreet = new Map<string, {citySlug: string; rows: IRcnAddressSuggestion[]}>();
    for (const row of [...base.rows, ...scopedForBase.flatMap(getScopedRows)]) {
        const key = getRcnAddressKey(row);
        const citySlug = getCitySlug(row.city);
        if (rowsByKey.has(key) || citySlug === null) {
            continue;
        }

        rowsByKey.set(key, row);
        const streetKey = getRcnStreetKey(row);
        const street = rowsByStreet.get(streetKey);
        if (street) {
            street.rows.push(row);
        } else {
            rowsByStreet.set(streetKey, {citySlug, rows: [row]});
        }
    }

    const queryCitySlugs = new Set(parsed.cityTokens.map((cityToken) => cityToken.citySlug));
    const streets: IPricesSearchStreetGroup[] = [];
    for (const [key, {citySlug, rows: streetRows}] of rowsByStreet) {
        const {city: rawCity, street: rawStreet} = streetRows[0];
        const score = scoreMatch(getRcnStreetMatchName(rawStreet), parsed.restTokens);
        // "łódź" alone lists the streets of Łódź, although no word of theirs was typed.
        const isCityQuery = parsed.restTokens.length === 0 && queryCitySlugs.has(citySlug);

        if (score >= PRICES_SEARCH_MATCH_SCORE.wordPrefix || isCityQuery) {
            streets.push({
                key,
                citySlug,
                rawCity,
                rawStreet,
                title: formatRcnStreetName(rawStreet),
                rows: streetRows.sort(compareNumbers),
                transactionsSum: streetRows.reduce((sum, row) => sum + row.transactions_count, 0),
                isComplete: isStreetComplete(key, citySlug, base, scopedForBase),
                score
            });
        }
    }

    const isQueryCity = (street: IPricesSearchStreetGroup): number => Number(queryCitySlugs.has(street.citySlug));
    const isContextCity = (street: IPricesSearchStreetGroup): number => Number(street.citySlug === context.citySlug);
    streets.sort(
        (a, b) =>
            b.score - a.score ||
            isQueryCity(b) - isQueryCity(a) ||
            isContextCity(b) - isContextCity(a) ||
            b.transactionsSum - a.transactionsSum ||
            a.title.localeCompare(b.title, "pl")
    );

    const {numberToken} = parsed;
    const addresses =
        numberToken === undefined
            ? []
            : streets.flatMap((street) => {
                  const matching = street.rows.filter((row) => houseNumberStartsWith(row.number, numberToken));
                  // The exact number first, then the rest of the street in natural order.
                  return [...matching.filter((row) => row.number === numberToken), ...matching.filter((row) => row.number !== numberToken)];
              });

    return {rows: [...rowsByKey.values()], streets, addresses};
};

/**
 * The best street shows its numbers right away when it is an exact match, or when all the streets
 * found share one name and the typed words are long enough to mean it ("Puławska" → Warszawa expanded).
 */
export const getAutoExpandedStreetKey = (parsed: IParsedPricesSearchQuery, streets: readonly IPricesSearchStreetGroup[]): string | null => {
    const [top] = streets;
    if (parsed.numberToken !== undefined || !top) {
        return null;
    }

    const distinctTitles = new Set(streets.map((street) => normalizeSearchText(street.title)));
    const isOnlyName = distinctTitles.size === 1 && parsed.restTokens.join(" ").length >= 3;

    return top.score === PRICES_SEARCH_MATCH_SCORE.exact || isOnlyName ? top.key : null;
};

/** `q` of the numbers request of a street: "tarnów bł. karoliny" → "tarnów karoliny". */
export const getStreetScopedQuery = (street: Pick<IPricesSearchStreetGroup, "rawCity" | "rawStreet">): string =>
    sanitizeRcnQueryTokens(`${street.rawCity} ${street.rawStreet}`);

/** `q` of a "more streets in <city>" request; null when it would repeat the base query (a city-only phrase). */
export const getCityScopedQuery = (parsed: IParsedPricesSearchQuery, cityName: string): string | null => {
    const query = sanitizeRcnQueryTokens([cityName, ...parsed.restTokens, ...(parsed.numberToken === undefined ? [] : [parsed.numberToken])].join(" "));

    return query === parsed.rcnQuery ? null : query;
};

/**
 * Cities "Pokaż więcej ulic" re-queries when the base response was capped: the city of the current page, otherwise
 * the cities with the most transactions among the streets found (at most PRICES_SEARCH_SHOW_MORE_MAX_CITIES).
 */
export const getShowMoreCitySlugs = (streets: readonly IPricesSearchStreetGroup[], context: IPricesSearchContext): string[] => {
    if (context.citySlug) {
        return [context.citySlug];
    }

    const transactionsByCity = new Map<string, number>();
    for (const street of streets) {
        transactionsByCity.set(street.citySlug, (transactionsByCity.get(street.citySlug) ?? 0) + street.transactionsSum);
    }

    return [...transactionsByCity.entries()]
        .sort((a, b) => b[1] - a[1])
        .slice(0, PRICES_SEARCH_SHOW_MORE_MAX_CITIES)
        .map(([citySlug]) => citySlug);
};
import {PRICES_SEARCH_SHOW_MORE_MAX_CITIES} from "../../constants/prices_search";
import type {
    IParsedPricesSearchQuery,
    IPricesAddressSearchResult,
    IPricesScopedAddressResult,
    IPricesSearchContext,
    IPricesSearchStreetGroup
} from "../../types/IPricesSearchModel";
import type {IRcnAddressKey, IRcnAddressSuggestion} from "../../types/IRcnAddress";
import {normalizeSearchText} from "../normalize_search_text";
import {compareHouseNumbers, houseNumberStartsWith} from "./compare_house_numbers";
import {formatRcnStreetName, getRcnStreetMatchName} from "./format_rcn_display_name";
import {findPricesCityByName} from "./resolve_query_diacritics";
import {sanitizeRcnQueryTokens} from "./sanitize_rcn_query_tokens";
import {PRICES_SEARCH_MATCH_SCORE, scoreMatch} from "./score_match";

const CITY_SCOPE_PREFIX = "city:";

/** `${city}|${street}` of the raw RCN values - the street group key and the scope key of its numbers request. */
export const getRcnStreetKey = ({city, street}: Pick<IRcnAddressKey, "city" | "street">): string => `${city}|${street}`;

/** `${city}|${street}|${number}` of the raw RCN values - shared by navigation ids, the recent address id and number prices. */
export const getRcnAddressKey = ({city, street, number}: IRcnAddressKey): string => `${city}|${street}|${number}`;

/** Scope key of a "more streets in this city" request. */
export const getPricesSearchCityScopeKey = (citySlug: string): string => `${CITY_SCOPE_PREFIX}${citySlug}`;

export interface IGroupedRcnAddresses {
    /** Rows of the 14 prices cities: the base response plus the scoped ones for the current `baseKey`, unique by address. */
    rows: IRcnAddressSuggestion[];
    /** Streets that match the query, best first. */
    streets: IPricesSearchStreetGroup[];
    /** Rows of those streets whose number starts with `parsed.numberToken`, best first; empty without a number. */
    addresses: IRcnAddressSuggestion[];
}

export interface IGroupRcnAddressesInput {
    parsed: IParsedPricesSearchQuery;
    base: IPricesAddressSearchResult;
    scoped: readonly IPricesScopedAddressResult[];
    context: IPricesSearchContext;
}

const getCitySlug = (rawCity: string): string | null => findPricesCityByName(rawCity)?.slug ?? null;

// A scoped response is only trusted for what it was asked for: the exact street, or the city.
const getScopedRows = (result: IPricesScopedAddressResult): IRcnAddressSuggestion[] => {
    if (result.scopeKey.startsWith(CITY_SCOPE_PREFIX)) {
        const citySlug = result.scopeKey.slice(CITY_SCOPE_PREFIX.length);

        return result.rows.filter((row) => getCitySlug(row.city) === citySlug);
    }

    return result.rows.filter((row) => getRcnStreetKey(row) === result.scopeKey);
};

// Complete = every number of the street is known: the base response was not capped, or a scoped request for the
// street (with rows of it - an empty answer falls back to the rows we have) or for its city was not capped.
const isStreetComplete = (key: string, citySlug: string, base: IPricesAddressSearchResult, scoped: readonly IPricesScopedAddressResult[]): boolean =>
    !base.capped ||
    scoped.some(
        (result) =>
            !result.capped &&
            ((result.scopeKey === key && result.rows.some((row) => getRcnStreetKey(row) === key)) || result.scopeKey === getPricesSearchCityScopeKey(citySlug))
    );

const compareNumbers = (a: IRcnAddressSuggestion, b: IRcnAddressSuggestion): number => compareHouseNumbers(a.number, b.number);

/**
 * Merges the base and the scoped rows, keeps the 14 prices cities (other cities have no prices
 * page), groups rows into streets, drops RCN's substring noise and ranks what is left - match score, a city named in
 * the query, the city of the current page, then transactions.
 */
export const groupRcnAddresses = ({parsed, base, scoped, context}: IGroupRcnAddressesInput): IGroupedRcnAddresses => {
    const scopedForBase = scoped.filter((result) => result.baseKey === parsed.baseKey);

    const rowsByKey = new Map<string, IRcnAddressSuggestion>();
    const rowsByStreet = new Map<string, {citySlug: string; rows: IRcnAddressSuggestion[]}>();
    for (const row of [...base.rows, ...scopedForBase.flatMap(getScopedRows)]) {
        const key = getRcnAddressKey(row);
        const citySlug = getCitySlug(row.city);
        if (rowsByKey.has(key) || citySlug === null) {
            continue;
        }

        rowsByKey.set(key, row);
        const streetKey = getRcnStreetKey(row);
        const street = rowsByStreet.get(streetKey);
        if (street) {
            street.rows.push(row);
        } else {
            rowsByStreet.set(streetKey, {citySlug, rows: [row]});
        }
    }

    const queryCitySlugs = new Set(parsed.cityTokens.map((cityToken) => cityToken.citySlug));
    const streets: IPricesSearchStreetGroup[] = [];
    for (const [key, {citySlug, rows: streetRows}] of rowsByStreet) {
        const {city: rawCity, street: rawStreet} = streetRows[0];
        const score = scoreMatch(getRcnStreetMatchName(rawStreet), parsed.restTokens);
        // "łódź" alone lists the streets of Łódź, although no word of theirs was typed.
        const isCityQuery = parsed.restTokens.length === 0 && queryCitySlugs.has(citySlug);

        if (score >= PRICES_SEARCH_MATCH_SCORE.wordPrefix || isCityQuery) {
            streets.push({
                key,
                citySlug,
                rawCity,
                rawStreet,
                title: formatRcnStreetName(rawStreet),
                rows: streetRows.sort(compareNumbers),
                transactionsSum: streetRows.reduce((sum, row) => sum + row.transactions_count, 0),
                isComplete: isStreetComplete(key, citySlug, base, scopedForBase),
                score
            });
        }
    }

    const isQueryCity = (street: IPricesSearchStreetGroup): number => Number(queryCitySlugs.has(street.citySlug));
    const isContextCity = (street: IPricesSearchStreetGroup): number => Number(street.citySlug === context.citySlug);
    streets.sort(
        (a, b) =>
            b.score - a.score ||
            isQueryCity(b) - isQueryCity(a) ||
            isContextCity(b) - isContextCity(a) ||
            b.transactionsSum - a.transactionsSum ||
            a.title.localeCompare(b.title, "pl")
    );

    const {numberToken} = parsed;
    const addresses =
        numberToken === undefined
            ? []
            : streets.flatMap((street) => {
                  const matching = street.rows.filter((row) => houseNumberStartsWith(row.number, numberToken));
                  // The exact number first, then the rest of the street in natural order.
                  return [...matching.filter((row) => row.number === numberToken), ...matching.filter((row) => row.number !== numberToken)];
              });

    return {rows: [...rowsByKey.values()], streets, addresses};
};

/**
 * The best street shows its numbers right away when it is an exact match, or when all the streets
 * found share one name and the typed words are long enough to mean it ("Puławska" → Warszawa expanded).
 */
export const getAutoExpandedStreetKey = (parsed: IParsedPricesSearchQuery, streets: readonly IPricesSearchStreetGroup[]): string | null => {
    const [top] = streets;
    if (parsed.numberToken !== undefined || !top) {
        return null;
    }

    const distinctTitles = new Set(streets.map((street) => normalizeSearchText(street.title)));
    const isOnlyName = distinctTitles.size === 1 && parsed.restTokens.join(" ").length >= 3;

    return top.score === PRICES_SEARCH_MATCH_SCORE.exact || isOnlyName ? top.key : null;
};

/** `q` of the numbers request of a street: "tarnów bł. karoliny" → "tarnów karoliny". */
export const getStreetScopedQuery = (street: Pick<IPricesSearchStreetGroup, "rawCity" | "rawStreet">): string =>
    sanitizeRcnQueryTokens(`${street.rawCity} ${street.rawStreet}`);

/** `q` of a "more streets in <city>" request; null when it would repeat the base query (a city-only phrase). */
export const getCityScopedQuery = (parsed: IParsedPricesSearchQuery, cityName: string): string | null => {
    const query = sanitizeRcnQueryTokens([cityName, ...parsed.restTokens, ...(parsed.numberToken === undefined ? [] : [parsed.numberToken])].join(" "));

    return query === parsed.rcnQuery ? null : query;
};

/**
 * Cities "Pokaż więcej ulic" re-queries when the base response was capped: the city of the current page, otherwise
 * the cities with the most transactions among the streets found (at most PRICES_SEARCH_SHOW_MORE_MAX_CITIES).
 */
export const getShowMoreCitySlugs = (streets: readonly IPricesSearchStreetGroup[], context: IPricesSearchContext): string[] => {
    if (context.citySlug) {
        return [context.citySlug];
    }

    const transactionsByCity = new Map<string, number>();
    for (const street of streets) {
        transactionsByCity.set(street.citySlug, (transactionsByCity.get(street.citySlug) ?? 0) + street.transactionsSum);
    }

    return [...transactionsByCity.entries()]
        .sort((a, b) => b[1] - a[1])
        .slice(0, PRICES_SEARCH_SHOW_MORE_MAX_CITIES)
        .map(([citySlug]) => citySlug);
};
